import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import {
  useGetCommentModerationSettingsQuery,
  useRunCommentModerationMutation,
  useUpdateCommentModerationSettingsMutation,
} from "~/ui-rtk/api/commentModerationApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Button from "~/ui-rtk/components/ui/controls/Button"
import CheckboxWithLabel from "~/ui-rtk/components/ui/controls/CheckboxWithLabel"
import Input from "~/ui-rtk/components/ui/controls/Input"
import CommentPullProbe from "./CommentPullProbe"

/** "RYZE, Four Sigmatic\nEveryday Dose" → ["RYZE", "Four Sigmatic", "Everyday Dose"] */
function parseTerms(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map(t => t.trim())
    .filter(Boolean)
}

/**
 * Border-only controls, no fill: the page's cards are translucent and so are
 * these. `INPUT_CLS` / `SELECT_CLS` override the shared controls' filled
 * background; `FIELD_CLS` is for the raw textareas (no shared textarea).
 */
export const FIELD_CLS =
  "border-basic-blue bg-transparent text-sm text-white placeholder:text-system-gray-600 focus:border-white/40 focus:outline-none"
export const INPUT_CLS = "h-9 border-basic-blue bg-transparent"
export const INPUT_CLASSES = {
  input: "font-normal text-white placeholder:text-system-gray-600",
}
export const SELECT_CLS =
  "select-sm h-9 w-auto border-basic-blue bg-transparent text-sm text-white"
export const CHECKBOX_CLS = "checkbox checkbox-sm"
const TEXTAREA_CLS = `textarea textarea-bordered min-h-[88px] w-full ${FIELD_CLS}`

const DIALOG_CLASSES = {
  container:
    "bg-basic-black border border-system-blue-600/40 p-6 rounded-xl text-left text-white max-h-[90vh] overflow-y-auto",
}

/**
 * The moderation rules and the manual run, on the report page (MD-5576).
 * A one-line bar shows whether the brand is in the daily sweep and runs the
 * job now (with the SAVED settings); "Settings" opens the rules in a modal:
 * the competitor list (word-boundary matched), extra spam terms, the
 * negativity pass, the hide switch, the Slack channel for the daily report,
 * the opt-in to the all-customers sweep, and the read-only pull probe.
 */
export default function ModerationSettings() {
  const { data, isLoading } = useGetCommentModerationSettingsQuery()
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateCommentModerationSettingsMutation()
  const [runModeration, { isLoading: isRunning }] =
    useRunCommentModerationMutation()

  const [open, setOpen] = useState(false)

  const [enabled, setEnabled] = useState(false)
  const [competitors, setCompetitors] = useState("")
  const [spamTerms, setSpamTerms] = useState("")
  const [hideNegative, setHideNegative] = useState(true)
  const [productContext, setProductContext] = useState("")
  const [hideComments, setHideComments] = useState(false)
  const [channelId, setChannelId] = useState("")
  /** Window for a manual run: how far back comments are read (the daily run uses 14). */
  // "0" = no window: a manual run reads every comment Meta returns. The
  // daily sweep keeps its own 14-day default; the ledger skips what was judged.
  const [runLookbackDays, setRunLookbackDays] = useState("0")

  useEffect(() => {
    if (!data) return
    setEnabled(data.enabled)
    setCompetitors(data.competitors.join("\n"))
    setSpamTerms(data.spamTerms.join("\n"))
    setHideNegative(data.hideNegative)
    setProductContext(data.productContext ?? "")
    setHideComments(data.hideComments)
    setChannelId(data.slackChannelId ?? "")
  }, [data])

  const handleSave = async () => {
    const trimmedChannel = channelId.trim()
    if (trimmedChannel && !/^[A-Za-z0-9]{1,32}$/.test(trimmedChannel)) {
      toast.error(
        "Slack channel must be a channel ID like C0123456789 (not a #name).",
      )
      return
    }
    try {
      await updateSettings({
        enabled,
        competitors: parseTerms(competitors),
        spamTerms: parseTerms(spamTerms),
        hideNegative,
        productContext: productContext.trim(),
        hideComments,
        slackChannelId: trimmedChannel,
      }).unwrap()
      toast.success(
        enabled
          ? `Comment moderation saved — this brand is in the daily sweep${
              hideComments
                ? " and flagged comments will be hidden."
                : " (analyse only: nothing is hidden on Meta)."
            }`
          : "Comment moderation saved. This brand is not in the daily sweep.",
      )
      setOpen(false)
    } catch (err) {
      const message = (err as { data?: { message?: string | string[] } })?.data
        ?.message
      toast.error(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Could not save the comment moderation settings.",
      )
    }
  }

  /** The typed window, clamped only when the run starts so the field can be
   *  edited freely. 0 or blank = no window (every comment Meta returns). */
  const lookbackDays = () => {
    if (String(runLookbackDays).trim() === "") return 0
    const typed = Math.round(Number(runLookbackDays))
    return Number.isFinite(typed) ? Math.min(3650, Math.max(0, typed)) : 14
  }

  const handleRun = async (preview: boolean) => {
    try {
      const analyseOnly = preview || !data?.hideComments
      await runModeration({
        dryRun: analyseOnly,
        commentLookbackDays: lookbackDays(),
      }).unwrap()
      toast.success(
        analyseOnly
          ? "Run started — comments are judged and reported; nothing changes on Meta."
          : "Run started — flagged comments are hidden and land in the report and in Slack.",
      )
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message
      toast.error(message || "Could not start the run. Please try again.")
    }
  }

  let status = ""
  if (data && !data.enabled) status = "Not in the daily sweep"
  else if (data?.hideComments) status = "Daily · hiding flagged comments"
  else if (data) status = "Daily · analyse only"

  return (
    <>
      <section className="flex flex-wrap items-center justify-between gap-system-sm rounded-lg border border-basic-blue bg-basic-dark-blue px-4 py-2">
        <div className="flex items-center gap-system-sm">
          <h2 className="text-sm font-medium">Moderation</h2>
          {status && (
            <span className="text-[11px] text-system-gray-500">{status}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-system-sm">
          <label
            className="flex items-center gap-1 text-xs text-system-gray-400"
            title="How far back a manual run reads comments on each post; 0 = all time (every comment Meta returns). The daily run uses 14 days; the ledger skips what was already judged."
          >
            comments from the last
            <Input
              type="number"
              min={0}
              max={3650}
              width={64}
              value={runLookbackDays}
              onChange={e => setRunLookbackDays(e.target.value)}
              onBlur={() => setRunLookbackDays(String(lookbackDays()))}
              className={INPUT_CLS}
              classes={INPUT_CLASSES}
            />
            days (0 = all)
          </label>
          <Button
            size="xs"
            variant={{ variant: "outlined" }}
            onClick={() => void handleRun(true)}
            disabled={isRunning || isSaving || isLoading}
            title="Judge the comments with the SAVED settings, hide nothing on Meta even if hiding is on"
          >
            {isRunning ? "Starting…" : "Run without hiding"}
          </Button>
          <Button
            size="xs"
            variant={{ variant: "outlined" }}
            onClick={() => void handleRun(false)}
            disabled={isRunning || isSaving || isLoading}
            title="Run the moderation now with the SAVED settings"
          >
            Run now
          </Button>
          <Button
            size="xs"
            variant={{ variant: "solid", color: "pink" }}
            onClick={() => setOpen(true)}
            disabled={isLoading}
          >
            Settings
          </Button>
        </div>
      </section>

      <Dialog
        isOpen={open}
        handleClose={() => setOpen(false)}
        width={760}
        classes={DIALOG_CLASSES}
        closeOnClickOutside
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold">Moderation settings</h3>
            <p className="mt-1 text-xs text-system-gray-400">
              Every day, comments on this brand&apos;s spending Facebook and
              Instagram ad posts are read and judged: competitor plugs, spam and
              comments that run the product down are flagged; questions, praise
              and neutral comments are kept. Hiding the flagged ones on Meta is
              a separate switch below (never deleted — the writer still sees
              them). Every decision lands in the report, where any comment can
              be unhidden.
            </p>
          </div>
          <Button
            size="xs"
            variant={{ variant: "text" }}
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </div>

        <div className="mt-4 flex flex-col gap-system-lg">
          {isLoading && !data ? (
            <p className="text-sm text-system-gray-400">Loading…</p>
          ) : (
            <>
              <CheckboxWithLabel
                className={CHECKBOX_CLS}
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
              >
                <span className="text-sm">
                  Moderate this brand&apos;s ad comments daily
                </span>
              </CheckboxWithLabel>

              <section className="flex flex-col gap-system-2xs">
                <label
                  htmlFor="cm-competitors"
                  className="text-sm font-medium text-system-gray-200"
                >
                  Competitors
                </label>
                <p className="text-xs text-system-gray-400">
                  One per line (or comma-separated). A comment naming one is
                  hidden. Matched on whole words, so &quot;rise&quot; never
                  matches &quot;RYZE&quot;.
                </p>
                <textarea
                  id="cm-competitors"
                  className={TEXTAREA_CLS}
                  value={competitors}
                  onChange={e => setCompetitors(e.target.value)}
                  placeholder={"RYZE\nFour Sigmatic\nEveryday Dose"}
                />
              </section>

              <section className="flex flex-col gap-system-2xs">
                <label
                  htmlFor="cm-spam"
                  className="text-sm font-medium text-system-gray-200"
                >
                  Extra spam terms (optional)
                </label>
                <p className="text-xs text-system-gray-400">
                  Affiliate pitches, promo codes, &quot;DM me&quot;, links and
                  messenger handles are caught already; add brand-specific
                  phrases here.
                </p>
                <textarea
                  id="cm-spam"
                  className={TEXTAREA_CLS}
                  value={spamTerms}
                  onChange={e => setSpamTerms(e.target.value)}
                  placeholder="one term per line"
                />
              </section>

              <CheckboxWithLabel
                className={CHECKBOX_CLS}
                checked={hideNegative}
                onChange={e => setHideNegative(e.target.checked)}
              >
                <span className="text-sm">
                  Also hide negative comments (bad experience, &quot;scam&quot;,
                  health-harm claims, mocking the founder or the ad). Skeptical
                  questions are always kept.
                </span>
              </CheckboxWithLabel>

              <section className="flex flex-col gap-system-2xs">
                <label
                  htmlFor="cm-context"
                  className="text-sm font-medium text-system-gray-200"
                >
                  Product context for the AI (optional)
                </label>
                <p className="text-xs text-system-gray-400">
                  A sentence or two on what the brand sells and how it is
                  usually criticised, so the model tells a complaint about the
                  product from a complaint about a competitor. The per-ad
                  audience summaries use it too.
                </p>
                <textarea
                  id="cm-context"
                  className={TEXTAREA_CLS}
                  value={productContext}
                  onChange={e => setProductContext(e.target.value)}
                  placeholder="e.g. Mushroom-based coffee alternative; founder Shane Heath appears in ads; common jabs: taste, price, 'tastes like dirt'."
                />
              </section>

              <section className="flex flex-col gap-system-2xs rounded-lg border border-basic-blue px-system-md py-system-sm">
                <CheckboxWithLabel
                  className={CHECKBOX_CLS}
                  checked={hideComments}
                  onChange={e => setHideComments(e.target.checked)}
                >
                  <span className="text-sm font-medium">
                    Hide flagged comments on Meta
                  </span>
                </CheckboxWithLabel>
                <p className="text-xs text-system-gray-400">
                  Off (default): analyse only — comments are judged and
                  recorded, the report, reactions and dashboard fill up, and
                  nothing changes on Meta. On: competitor, spam
                  {hideNegative ? " and negative" : ""} comments are hidden on
                  the Page; any of them can be unhidden from the report.
                </p>
              </section>

              <section className="flex flex-col gap-system-2xs">
                <label
                  htmlFor="cm-channel"
                  className="text-sm font-medium text-system-gray-200"
                >
                  Slack channel for the daily report (optional)
                </label>
                <p className="text-xs text-system-gray-400">
                  Channel ID (e.g. C0123456789). Leave empty for the default
                  jobs channel. The Marathon bot must be invited.
                </p>
                <Input
                  id="cm-channel"
                  width={320}
                  value={channelId}
                  onChange={e => setChannelId(e.target.value)}
                  placeholder="Defaults to the jobs channel"
                  className={INPUT_CLS}
                  classes={INPUT_CLASSES}
                />
              </section>

              <CommentPullProbe />
            </>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-system-sm border-t border-basic-blue pt-4">
          <Button
            size="sm"
            variant={{ variant: "text" }}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant={{ variant: "solid", color: "pink" }}
            onClick={() => void handleSave()}
            disabled={isSaving || isLoading}
            isLoading={isSaving}
          >
            {isSaving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </Dialog>
    </>
  )
}
